import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  BookOpen, Clock, CheckCircle, AlertCircle,
  Calendar, Send, Loader2, FileText, Paperclip,
  Download, RotateCcw, Star, Upload, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [submittingId, setSubmittingId] = useState(null);
  const [submissionDrafts, setSubmissionDrafts] = useState({});
  const [uploadingFor, setUploadingFor] = useState(null);
  // submissionFiles: { [activityId]: string[] } — list of uploaded file URLs
  const [submissionFiles, setSubmissionFiles] = useState({});

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/activities?page_size=200`);
      setActivities(response.data.data ?? response.data);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (activityId, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File must be under 20 MB');
      return;
    }

    setUploadingFor(activityId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/student/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Build absolute URL so it works regardless of frontend/backend origin
      const url = res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}`;
      setSubmissionFiles(prev => ({
        ...prev,
        [activityId]: [...(prev[activityId] || []), url]
      }));
      toast.success('File uploaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'File upload failed';
      toast.error(msg);
    } finally {
      setUploadingFor(null);
    }
  };

  const removeFile = (activityId, index) => {
    setSubmissionFiles(prev => ({
      ...prev,
      [activityId]: (prev[activityId] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (activityId) => {
    const submissionContent = (submissionDrafts[activityId] || '').trim();
    const files = submissionFiles[activityId] || [];

    if (!submissionContent && files.length === 0) {
      toast.error('Please enter your submission or upload a file');
      return;
    }

    setSubmittingId(activityId);
    try {
      await axios.post(`${API_URL}/api/student/activities/${activityId}/submit`, {
        submission_content: submissionContent || null,
        submission_files: files
      });
      toast.success('Activity submitted successfully!');
      setSubmissionDrafts(prev => {
        const next = { ...prev };
        delete next[activityId];
        return next;
      });
      setSubmissionFiles(prev => {
        const next = { ...prev };
        delete next[activityId];
        return next;
      });
      fetchActivities();
    } catch (err) {
      console.error('Failed to submit activity:', err);
      const msg = err.response?.data?.detail || 'Failed to submit activity';
      toast.error(msg);
    } finally {
      setSubmittingId(null);
    }
  };

  const filteredActivities = activities.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'pending') return !a.submitted || a.review_status === 'needs_revision';
    if (filter === 'submitted') return a.submitted && a.review_status === 'pending';
    if (filter === 'needs_revision') return a.review_status === 'needs_revision';
    if (filter === 'graded') return a.review_status === 'reviewed';
    return true;
  });

  const getStatusBadge = (activity) => {
    if (activity.review_status === 'needs_revision') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#F5A623]/10 text-[#F5A623] flex items-center gap-1">
          <RotateCcw size={10} />
          Needs Revision
        </span>
      );
    }
    if (activity.review_status === 'reviewed' && activity.grade !== null && activity.grade !== undefined) {
      return (
        <span className={`
          px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1
          ${activity.grade >= 80 ? 'bg-[#4CAF50]/10 text-[#4CAF50]' :
            activity.grade >= 60 ? 'bg-[#F5A623]/10 text-[#F5A623]' : 'bg-[#D72B2B]/10 text-[#D72B2B]'}
        `}>
          <Star size={10} />
          Grade: {activity.grade}%
        </span>
      );
    }
    if (activity.submitted) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#1A2E6E]/10 text-[#1A2E6E] flex items-center gap-1">
          <CheckCircle size={10} />
          Submitted
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#E8EAF0] text-[#1A2E6E]/60 flex items-center gap-1">
        <Clock size={10} />
        Pending
      </span>
    );
  };

  // Decide whether to show the submission form
  const canSubmit = (activity) => {
    if (!activity.submitted) return true;
    if (activity.review_status === 'needs_revision') return true;
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-activities-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          Activities
        </h1>
        <p className="text-[#1A2E6E]/60 mt-1">
          View and submit your assigned activities
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white rounded-xl p-1.5 border border-[#E8EAF0] w-fit flex-wrap">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'submitted', label: 'Submitted' },
          { id: 'needs_revision', label: 'Needs Revision' },
          { id: 'graded', label: 'Graded' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === tab.id
                ? 'bg-[#1A2E6E] text-white'
                : 'text-[#1A2E6E]/70 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Activities List */}
      {filteredActivities.length > 0 ? (
        <div className="space-y-4">
          {filteredActivities.map((activity, index) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden"
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Icon */}
                  <div className={`
                    w-14 h-14 rounded-xl flex items-center justify-center shrink-0
                    ${activity.review_status === 'needs_revision' ? 'bg-[#F5A623]/10' :
                      activity.review_status === 'reviewed' ? 'bg-[#4CAF50]/10' :
                      activity.submitted ? 'bg-[#1A2E6E]/10' : 'bg-[#E8EAF0]'}
                  `}>
                    <BookOpen className={`w-7 h-7 ${
                      activity.review_status === 'needs_revision' ? 'text-[#F5A623]' :
                      activity.review_status === 'reviewed' ? 'text-[#4CAF50]' :
                      activity.submitted ? 'text-[#1A2E6E]' : 'text-[#1A2E6E]/40'
                    }`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-semibold text-[#1A2E6E] text-lg">
                        {activity.title}
                      </h3>
                      {getStatusBadge(activity)}
                    </div>

                    <p className="text-sm text-[#1A2E6E]/70 mb-3">
                      {activity.description}
                    </p>

                    {/* Teacher Attachment */}
                    {activity.attachment_url && (
                      <div className="mb-3">
                        <a
                          href={activity.attachment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-[#1A2E6E] hover:text-[#D72B2B] bg-[#E8EAF0] px-3 py-1.5 rounded-lg transition-colors font-medium"
                        >
                          <Paperclip size={14} />
                          Teacher Resource
                          <Download size={12} />
                        </a>
                      </div>
                    )}

                    {activity.due_date && (
                      <div className="flex items-center gap-1.5 text-sm text-[#1A2E6E]/60 mb-4">
                        <Calendar className="w-4 h-4" />
                        Due: {new Date(activity.due_date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </div>
                    )}

                    {/* Needs Revision Banner */}
                    {activity.review_status === 'needs_revision' && (
                      <div className="bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-xl p-4 mb-4 flex items-start gap-3">
                        <RotateCcw className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-[#F5A623] mb-1">Revision Required</p>
                          {activity.feedback && (
                            <p className="text-sm text-[#1A2E6E]/80">{activity.feedback}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Feedback (if reviewed/graded) */}
                    {activity.review_status === 'reviewed' && activity.feedback && (
                      <div className="bg-[#4CAF50]/5 border border-[#4CAF50]/20 rounded-xl p-4 mb-4">
                        <p className="text-sm font-medium text-[#4CAF50] mb-1">Teacher Feedback:</p>
                        <p className="text-sm text-[#1A2E6E]/80">{activity.feedback}</p>
                      </div>
                    )}

                    {/* Previous Submission (if submitted) */}
                    {activity.submitted && activity.submission_content && (
                      <div className="bg-[#1A2E6E]/5 border border-[#1A2E6E]/10 rounded-xl p-4 mb-4">
                        <p className="text-sm font-medium text-[#1A2E6E] mb-1">
                          {activity.review_status === 'needs_revision' ? 'Previous Submission:' : 'Your Submission:'}
                        </p>
                        <p className="text-sm text-[#1A2E6E]/80">{activity.submission_content}</p>
                        {activity.submission_files?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {activity.submission_files.map((url, i) => (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-[#1A2E6E] hover:text-[#D72B2B] bg-white border border-[#E8EAF0] px-2 py-1 rounded"
                              >
                                <Download size={10} />
                                File {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                        {activity.submitted_at && (
                          <p className="text-xs text-[#1A2E6E]/50 mt-2">
                            Submitted: {new Date(activity.submitted_at).toLocaleString('en-GB')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Submission Form */}
                    {canSubmit(activity) && (
                      <div className={`mt-4 pt-4 border-t border-[#E8EAF0] ${
                        activity.review_status === 'needs_revision' ? 'border-[#F5A623]/30' : ''
                      }`}>
                        <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                          {activity.review_status === 'needs_revision' ? 'Revised Submission' : 'Your Submission'}
                        </label>
                        <textarea
                          value={submissionDrafts[activity.id] || ''}
                          onChange={(e) => setSubmissionDrafts(prev => ({
                            ...prev,
                            [activity.id]: e.target.value
                          }))}
                          placeholder="Type your answer or submission here..."
                          className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none resize-none"
                          rows={4}
                        />

                        {/* File Upload */}
                        <div className="mt-3">
                          <label className="flex items-center gap-2 cursor-pointer w-fit">
                            <span className="flex items-center gap-1.5 text-sm text-[#1A2E6E]/70 hover:text-[#1A2E6E] transition-colors">
                              {uploadingFor === activity.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Upload size={16} />
                              )}
                              Attach a file
                            </span>
                            <input
                              type="file"
                              className="hidden"
                              disabled={uploadingFor === activity.id}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileUpload(activity.id, e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                            />
                          </label>
                          {(submissionFiles[activity.id] || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {(submissionFiles[activity.id] || []).map((url, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-1 text-xs text-[#1A2E6E] bg-[#E8EAF0] px-2 py-1 rounded"
                                >
                                  <FileText size={10} />
                                  File {i + 1}
                                  <button
                                    type="button"
                                    onClick={() => removeFile(activity.id, i)}
                                    className="ml-1 text-[#1A2E6E]/50 hover:text-[#D72B2B]"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => handleSubmit(activity.id)}
                          disabled={
                            submittingId === activity.id ||
                            (!(submissionDrafts[activity.id] || '').trim() && !(submissionFiles[activity.id] || []).length)
                          }
                          className={`mt-3 flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition-colors font-medium disabled:opacity-50 ${
                            activity.review_status === 'needs_revision'
                              ? 'bg-[#F5A623] hover:bg-[#E09000]'
                              : 'bg-[#D72B2B] hover:bg-[#B71C1C]'
                          }`}
                        >
                          {submittingId === activity.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : activity.review_status === 'needs_revision' ? (
                            <RotateCcw className="w-4 h-4" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          {activity.review_status === 'needs_revision' ? 'Resubmit Activity' : 'Submit Activity'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <BookOpen className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#1A2E6E] mb-2">No Activities Found</h3>
          <p className="text-[#1A2E6E]/60">
            {filter === 'pending'
              ? "Great job! You don't have any pending activities."
              : filter === 'submitted'
                ? 'No activities awaiting review.'
                : filter === 'needs_revision'
                  ? 'No activities need revision right now.'
                  : filter === 'graded'
                    ? 'No reviewed activities yet.'
                    : 'No activities have been assigned to you yet.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default StudentActivities;
